import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { API_IDS, ITEM_CATEGORY } from '@intigral/constant';

import {
    IAddBookMarkPayload,
    IBookMarkResponse,
    IBookMarkSearchParams,
    IBookMarkDetailResponse,
    IWatchAgainSearchParams,
    IWatchAgainResponse,
} from '../../entity/interface/iBookmark';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { DI } from '@intigral/constant/config/DIContainer';
import { IBookmarkRepository } from '../../core/repositories/interfaces/iBookmarkRepository';
import * as CryptoJS from 'crypto-js';
import { getConfigurationFromCache } from '../../services/ConfigurationService';
class BookmarkRepository implements IBookmarkRepository {
    private static readonly SECRET_KEY = process.env['NX_PUBLIC_BOOKMARK_SECRET_KEY'] || '';

    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    private static hashHmacSha256(data: string, key: string): string {
        try {
            const hmac = CryptoJS.HmacSHA256(data, key);
            const token = CryptoJS.enc.Base64.stringify(hmac);
            return token;
        } catch (err) {
            // Handle errors gracefully, e.g., log them
            return ''; // Return an empty string or handle as needed
        }
    }

    async addBookmark(
        apiKey: string,
        profileGuid: string,
        payload: IAddBookMarkPayload,
        token: string,
        identityToken: string
    ): Promise<IBookMarkResponse> {
        //  FIXED: Get configuration from React Query via configuration service
        const config = getConfigurationFromCache();
        const isGamificationEnabled = config?.gamificationConfigs?.enableGamification || false;
        // For gamification, validate required fields and calculate missing values
        if (isGamificationEnabled) {
            // Get segment size from CMS gamification configs, fallback to 120 seconds (2 minutes)
            const segmentSize = config?.gamificationConfigs?.assetSegmentSizeInSeconds || 120;
            // Calculate segments only for VOD and rewind content, not for live TV (linear)
            const isVod = payload.programType === ITEM_CATEGORY.VOD;

            // For VOD, calculate segments based on timecode and segment size
            if (!isVod) {
                const segmentId = Math.floor(Number(payload.timecode) / segmentSize);
                payload.segments = [segmentId];
            }

            // Add epoch timestamp if not provided
            if (!payload.epochMs) {
                payload.epochMs = Date.now();
            }

            // Generate requestId if not provided
            if (!payload.requestId) {
                payload.requestId = crypto.randomUUID();
            }

            // Calculate HMAC token if not provided
            if (!isVod) {
                const segmentIds = payload.segments?.join('|') || '';
                const tokenPayload = `${payload.epochMs}:${payload.requestId}:${segmentIds}:${payload.assetid}:${
                    payload.paId || ''
                }`;
                // Use proper HMAC-SHA256 token generation
                payload.token = BookmarkRepository.hashHmacSha256(tokenPayload, BookmarkRepository.SECRET_KEY);
            }
        }

        const response = await this.http.post<IBookMarkResponse>(this.apiPathManager.getPath(API_IDS.BOOKMARK_API), {
            params: {
                apiKey,
                profileGuid,
            },
            body: {
                ...payload,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });

        return response.data;
    }

    async getBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string
    ): Promise<IBookMarkResponse> {
        const response = await this.http.get<IBookMarkResponse>(this.apiPathManager.getPath(API_IDS.BOOKMARK_API), {
            params: {
                apiKey,
                profileGuid,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });

        return response.data;
    }

    async getDetailBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        searchParams: IBookMarkSearchParams,
        datasource?: string
    ): Promise<IBookMarkDetailResponse> {
        const url = this.apiPathManager.getByDatasource(datasource || '');
        const response = await this.http.get<IBookMarkDetailResponse>(url, {
            params: {
                apiKey,
                profileGuid,
                country: searchParams.country,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });

        return response.data;
    }

    async deleteBookmark(
        apiKey: string,
        profileGuid: string,
        token: string,
        identityToken: string,
        assetId: string,
        paId: string,
        src?: string
    ): Promise<IBookMarkResponse> {
        const response = await this.http.delete<IBookMarkResponse>(this.apiPathManager.getPath(API_IDS.BOOKMARK_API), {
            params: {
                apiKey,
                profileGuid,
                assetId,
                ...(src ? { src } : {}),
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });

        return response.data;
    }

    async getWatchAgain(
        apiKey: string,
        profileName: string,
        token: string,
        identityToken: string,
        searchParams: IWatchAgainSearchParams
    ): Promise<IWatchAgainResponse> {
        const response = await this.http.get<IWatchAgainResponse>(
            this.apiPathManager.getPath(API_IDS.BOOKMARK_API_WATCH_AGAIN),
            {
                params: {
                    apiKey,
                    profileName,
                    ...searchParams,
                },
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
            }
        );

        return response.data;
    }
}

export default BookmarkRepository;
